import {
  AIGenerationResponseType,
  AllOrderlinesStats,
  AudienceType,
  CampaignDetailsType,
  CampaignType,
  CreativeType,
  GeneratedCampaignType,
  ResponseType,
  TCampaigns,
  TCampaignStats,
  TCampaignsUserStats,
  TCampaignTargets,
  TCreativeTypesUpload,
  TDeleteCampaigns,
  TOrderlines,
  TProspectsUpload,
  TSaveCampaign,
  TTargetAllGoalAudiences,
  TTargetAudience,
  TTargetAudiencesByGoal,
  TTotalCounts,
} from 'types';
import { GoalSectionTitles } from 'enums';
import requests from '../http';
import { createCreative } from './creativeMethods';

export const createCampaign = async (body: {
  name: string;
  audience_id?: number;
  goal?: GoalSectionTitles;
}): Promise<ResponseType<TSaveCampaign>> => {
  return requests.post(`/campaigns/save`, body);
};

export const getCampaigns = async (
  page?: number | null,
  size?: number | null,
  search?: string | null,
  sort_key: string | null = null,
  order: string | null = null,
  draft = true
): Promise<ResponseType<TCampaigns>> => {
  let req = `/campaigns?page=${page}&size=${size}`;

  if (sort_key) req += `&sort_key=${sort_key}&order=${order}`;
  if (search) req += `&search=${encodeURIComponent(search)}`;
  if (!draft) req += `&draft=${draft}`;

  return requests.get(req);
};

export const getOrderlines = async (
  page?: number | null,
  size?: number | null,
  search?: string | null,
  sort_key: string | null = null,
  order: string | null = null
): Promise<ResponseType<TOrderlines>> => {
  let req = `/orderline/user-orderlines?page=${page}&size=${size}`;

  if (sort_key) req += `&sort_key=${sort_key}&order=${order}`;
  if (search) req += `&search=${encodeURIComponent(search)}`;

  return requests.get(req);
};

export const getTotalCounts = async (): Promise<ResponseType<TTotalCounts>> => {
  return requests.get(`/orderline/user-orderlines-and-cmapaigns-count`);
};
export const getCampaignAudience = async (
  id?: string | undefined,
  page?: number | null,
  size?: number | null,
  showContact?: Boolean
): Promise<ResponseType<TTargetAudience>> => {
  return requests.get(
    `/campaigns/all/target_audience?campaign_id=${id}&page=${page}&size=${size}&show_contact=${showContact}`
  );
};
export const uploadCSV = async (body: Object | any): Promise<ResponseType<TProspectsUpload>> => {
  return requests.post(`/campaigns/prospects/upload`, body, true);
};

export const saveCampaignTarget = async (
  body: Object | any,
  campaign_id?: string | number | undefined
): Promise<ResponseType<TCampaignTargets>> => {
  return requests.post(`/campaigns/targets?campaign_id=${campaign_id}`, body);
};

export const deleteCampaign = async (body: Object): Promise<ResponseType<TDeleteCampaigns>> => {
  return requests.post(`/campaigns/delete-campaigns`, body);
};
export const saveCampaignCreative = async (
  body: FormData
): Promise<ResponseType<TCreativeTypesUpload>> => {
  return requests.put('/creative/upload', body, true);
};

export const getCampaignById = async (campaign_id: string): Promise<ResponseType<CampaignType>> => {
  return requests.get(`/campaigns/${campaign_id}`);
};

export const getGeneratedCampaignById = async (
  campaign_id: string
): Promise<ResponseType<GeneratedCampaignType>> => {
  return requests.get(`/campaigns/generated/${campaign_id}`);
};
export const getCampaignDetailsById = async (
  campaign_id: string
): Promise<ResponseType<CampaignDetailsType>> => {
  return requests.get(`/campaigns/details/${campaign_id}`);
};

export const getSavedCampaignAudienceById = async (
  campaign_id: string
): Promise<ResponseType<CampaignType>> => {
  return requests.get(`/campaigns/get_saved_targets?campaign_id=${campaign_id}`);
};

export const updateCampaign = async (
  campaign_id: string,
  body: Partial<CampaignType>
): Promise<ResponseType<CampaignType>> => {
  return requests.patch(`/campaign/${campaign_id}`, body);
};
export const postCampaignBudget = async (
  campaign_id: string,
  body: Partial<CampaignType>
): Promise<ResponseType<CampaignType>> => {
  return requests.post(`/campaigns/update_campaign_budget?campaign_id=${campaign_id}`, body);
};
export const sendLinkViaEmail = async (
  campaign_id: string
): Promise<ResponseType<{ msg: string }>> => {
  return requests.post(`/campaigns/${campaign_id}/send-link`);
};
export const checkoutSubmitGeneratedCampaign = async (
  campaign_id: string,
  body: FormData
): Promise<ResponseType<CampaignType>> => {
  return requests.post(`/campaigns/checkout-submit?campaign_id=${campaign_id}`, body, true);
};
export const attachAudience = async (
  campaign_id: string,
  body: AudienceType[]
): Promise<ResponseType<CampaignType>> => {
  return requests.patch(`/campaign/attach-audience/${campaign_id}`, body);
};

export const detachAudience = async (
  campaign_id: string,
  body: AudienceType[]
): Promise<ResponseType<CampaignType>> => {
  return requests.patch(`/campaign/detach-audience/${campaign_id}`, body);
};

export const attachCreative = async (
  campaign_id: string,
  body: CreativeType[]
): Promise<ResponseType<CampaignType>> => {
  return requests.patch(`/campaign/attach-creative/${campaign_id}`, body);
};

export const detachCreative = async (
  campaign_id: string,
  body: CreativeType[]
): Promise<ResponseType<CampaignType>> => {
  return requests.patch(`/campaign/detach-creative/${campaign_id}`, body);
};

export const createAndAttachCreatives = async (
  body: Partial<CreativeType>[],
  campaign_id: string
) => {
  body.map(creative =>
    createCreative(creative).then(res => {
      if (res.data) attachCreative(campaign_id, [res.data]);
    })
  );
};

export const getCompaignGraphData = async (): Promise<ResponseType<TCampaignsUserStats>> =>
  requests.get('/campaigns/user-stats');

export const getAllOrderlinesStats = async (): Promise<ResponseType<AllOrderlinesStats>> =>
  requests.get('/orderline');

export const getCompaignPerformance = async (id: number): Promise<ResponseType<TCampaignStats>> =>
  requests.get(`/campaigns/campaign-stats/${id}`);

export const getCampaignAudiencesByGoal = async (
  goalName: GoalSectionTitles,
  id: string,
  page?: number | null,
  size?: number | null
): Promise<ResponseType<TTargetAudiencesByGoal>> => {
  return requests.get(`/campaigns/${id}/targets/${goalName}?page=${page}&size=${size}`);
};

export const getCampaignTargets = async (
  id: string,
  page?: number | null,
  size?: number | null
): Promise<ResponseType<TTargetAllGoalAudiences>> => {
  return requests.get(`/campaigns/${id}/targets?page=${page}&size=${size}`);
};

export const getBannerPrompts = async (
  campaign_id: number,
  banner_uuid: string
): Promise<ResponseType<AIGenerationResponseType[]>> => {
  return requests.get(`/campaigns/${campaign_id}/banner/${banner_uuid}/prompt`);
};

export const sendPrompt = async (
  campaign_id: string,
  banner_uuid: string,
  body: string
): Promise<ResponseType<AIGenerationResponseType>> => {
  return requests.post(`/campaigns/${campaign_id}/banner/${banner_uuid}/prompt`, body);
};

export const approvePrompt = async (
  campaign_id: string,
  banner_uuid: string,
  prompt_uuid: string
): Promise<ResponseType<AIGenerationResponseType>> => {
  return requests.patch(`/campaigns/${campaign_id}/banner/${banner_uuid}/prompt/${prompt_uuid}`);
};
export const applyToPrompt = async (
  campaign_id: string,
  banner_uuid: string,
  prompt_uuid: string
): Promise<ResponseType<AIGenerationResponseType>> => {
  return requests.put(
    `/campaigns/${campaign_id}/banner/${banner_uuid}/prompt/${prompt_uuid}/auto-apply`
  );
};
export const makeChangePrompt = async (
  campaign_id: string,
  banner_uuid: string,
  prompt_uuid: string
): Promise<ResponseType<AIGenerationResponseType>> => {
  return requests.patch(
    `/campaigns/${campaign_id}/banner/${banner_uuid}/prompt/${prompt_uuid}/create-selected`
  );
};
